import { distinctLegalName } from '../../../utils/vendorName';
import { getQuestionnaireLabel } from '../../../utils/questionnaireOptions';
import React from 'react';
import Icon from '../../../components/AppIcon';
import { Card, CardContent } from '../../../components/ui/Card';
import { getCountryName, isIndia } from '../../../utils/countries';
import { natureLabel, oemOdmLabel, subcategoryLabel } from '../../../utils/classification';
import {
  formatSupplierCategory,
  formatSupplierGroup,
  formatMsmeStatus,
  formatBusinessVertical,
  formatEmployeeCount,
  formatAnnualTurnover,
} from '../../../utils/formatters';

const CompanyDetailsTab = ({ vendor, approvalHistory = [] }) => {
  // Helper function to ensure website URL has proper protocol
  const getWebsiteUrl = (website) => {
    if (!website) return '';
    if (website.startsWith('http://') || website.startsWith('https://')) {
      return website;
    }
    return `https://${website}`;
  };

  const formatPaymentTerms = (value) => {
    if (!value) return 'Not specified';
    const paymentTermsMap = {
      '10DAYS': '10 Days',
      '10ADV80AD10AI': '10% Adv 80% against despatch & 10% balance after installation',
      '10ADV90AF180DI': '10% Advance and 90% after 180 days from date of Invoice',
      '10ADVTTPIBAL90LC': '10% Advance Payment against PI by TT, balance 90% through LC',
      '100ADV': '100% Advance',
      '100AFDEL': '100% after delivery',
      '100TTAGTBL': '100% TT Against BL Copy',
      '12ADWG8ATY': '12% advance with GST, 8% after 1st year and Balance 80% after',
      '15DAYSI': '15 DAYS from Invoice',
      '15ADV75B10AI': '15% advance, 75% by TT before delivery & 10% after installation',
      '20ADV153520': '20% Adv, 15% after, 20% on Trial, 20% work submission & 20%',
      '20ADV70AD10AI': '20% Adv 70% against despatch & 10% balance after installation',
      '20ADV70R10H': '20% Advance, 70% on the readiness of the Material, 10% Hold',
      '20ADV75LC360R5R': '20% advance 75% LC 360 days 5% payment retention till warranty',
      '20ADV80SD': '20% Advance and 80% against shipping documents',
      '20ADV80BD': '20% Advance and Balance On Delivery',
      '20ADV1555PIB10': '20% Advance, 15% after 1 Month, 55% against PI, Balance 10%',
      '30DAYS': '30 Days',
      '45DAYS': '45 Days',
      '60DAYS': '60 Days',
      '90DAYS': '90 Days',
      '120DAYS': '120 Days',
      '180DAYS': '180 Days',
      '360DAYS': '360 Days',
      'ADVANCE': 'Advance',
      'CASH': 'Cash',
      'CHEQUE': 'Cheque',
      'CREDIT': 'Credit',
      'LC': 'LC',
      'NEFT/RTGS': 'NEFT/RTGS',
      'PDC': 'PDC',
      'TT': 'TT',
    };
    // The shared questionnaire options know every ERP code (this local map is older and partial).
    return paymentTermsMap[value] || getQuestionnaireLabel('supplier_term_of_payment', value);
  };

  const formatPaymentMethod = (value) => {
    if (!value) return 'Not specified';
    const paymentMethodMap = {
      CASH: 'Cash',
      CHEQUE: 'Cheque',
      'NEFT/RTGS': 'NEFT/RTGS',
      TT: 'TT',
      LC: 'LC',
      PDC: 'PDC',
    };
    return paymentMethodMap[value] || value;
  };

  const formatDeliveryTerms = (value) => {
    if (!value) return 'Not specified';
    const deliveryTermsMap = {
      'AS PER SCH': 'AS PER SCH',
      CIF: 'CIF',
      EXTRA: 'EXTRA',
      EXW: 'EXW',
      EXWDDN: 'EXWDDN',
      FOB: 'FOB',
      FOR: 'FOR',
      IN: 'IN',
      LCL: 'LCL',
      'TO PAID': 'TO PAID',
      'TO PAY': 'TO PAY',
    };
    return deliveryTermsMap[value] || value;
  };

  const formatDeliveryMode = (value) => {
    if (!value) return 'Not specified';
    const deliveryModeMap = {
      'BY AIR': 'By Air',
      'BY COURIER': 'By Courier',
      'BY HAND': 'By Hand',
      'BY ROAD': 'By Road',
      'BY SEA': 'By Sea',
    };
    return deliveryModeMap[value] || value;
  };

  const formatSupplierGroupLocal = (value) => {
    if (!value) return 'Not specified';
    const supplierGroupMap = {
      'oem-customer': 'OEM-Customer Referred Supplier',
      'odm-amber': 'ODM-Amber Developed Supplier',
      'CR-AGT': 'CR-AGT | Creditors Agents',
      'CR-BANK': 'CR-BANK | Creditors Bank',
      'CR-CAP': 'CR-CAP | Creditors Capex',
      'CR-CO': 'CR-CO | Creditors Consumable',
      'CR-EX': 'CR-EX | Creditors Expense',
      'CR-INT': 'CR-INT | Creditors Inter Unit',
      'CR-JW': 'CR-JW | Creditors Job work',
      'CR-KMP': 'CR-KMP | Creditors Key Management Persons',
      'CR-PK': 'CR-PK | Creditors Packing Material',
      'CR-RM': 'CR-RM | Creditors Raw Material',
      'CR-RPT': 'CR-RPT | Creditors Related Parties',
      'CR-SC': 'CR-SC | Creditors Sister Concern',
      'CR-IMP': 'CR-IMP | Creditors Imports Raw Material',
      'CR-IMP-CAP': 'CR-IMP-CAP | Creditors Import Capex',
      'CR-IMP-EXP': 'CR-IMP-EXP | Creditors Import Expense',
      'CR-IMP-SER': 'CR-IMP-SER | Creditors Import Service',
    };
    return supplierGroupMap[value] || value;
  };

  const formatCommodityCode = (value) => {
    if (!value) return 'Not specified';
    const commodityCodeMap = {
      AA: 'AA - Aluminium',
      AB: 'AB - FG-Extruded Sheet',
      AC: 'AC - FG-HE Coil',
      AD: 'AD - FG-IDU',
      AE: 'AE - FG-Inner Case',
      AF: 'AF - FG-MFC',
      AG: 'AG - FG-ODU',
      AH: 'AH - FG-PLC',
      AI: 'AI - FG-SMC',
      AJ: 'AJ - Steel',
      AK: 'AK - FG-WAC',
      AL: 'AL - FG-ODU Kit',
      AM: 'AM - FG-SAC',
      AN: 'AN - ODU-Accessories',
      AO: 'AO - Compressor',
      AP: 'AP - Motor',
      AQ: 'AQ - RM-IDU',
      AR: 'AR - RM-HE Coil',
      AS: 'AS - Sticker & Label',
      AT: 'AT - Remote',
      AU: 'AU - Foam',
      AV: 'AV - EPS',
      AX: 'AX - Brass Parts',
      AY: 'AY - Capacitor',
      AZ: 'AZ - Cross Flow Fan',
      BA: 'BA - FG-Portable AC',
      BB: 'BB - Fan',
      BC: 'BC - Gas',
      BD: 'BD - Hardware',
      BE: 'BE - Ink',
      BF: 'BF - Poly Bag',
      BG: 'BG - Rear Grill',
      BH: 'BH - Hardware Other',
      BI: 'BI - Rubber Parts',
      BJ: 'BJ - Service Valve',
      BK: 'BK - Tape',
      BL: 'BL - Wire',
      BM: 'BM - FG-PP Roll',
      BN: 'BN - Bolt',
      BO: 'BO - Brazing Rod',
      BP: 'BP - Carton Box',
      BQ: 'BQ - Chemical',
      BR: 'BR - FG-Copper Tubing',
      BS: 'BS - Powder',
      BT: 'BT - Terminal Block',
      BU: 'BU - Packing Material',
      BV: 'BV - Copper Tube',
    };
    return commodityCodeMap[value] || value;
  };

  const getQuestionnaireData = () => {
    const questionnaireEntry = approvalHistory.find(
      (event) => event.comments && event.comments.includes('Questionnaire completed:')
    );

    if (!questionnaireEntry) {
      return {};
    }

    const questionnaireString = questionnaireEntry.comments.replace('Questionnaire completed: ', '');
    const questionnairePairs = questionnaireString.split(', ');

    const questionnaireData = {};
    questionnairePairs.forEach((pair) => {
      const [key, value] = pair.split(': ');
      if (key && value) {
        questionnaireData[key] = value;
      }
    });

    return questionnaireData;
  };

  const questionnaireData = getQuestionnaireData();

  const companyDetails = {
    basicInfo: {
      title: 'Basic Information',
      icon: 'Building2',
      fields: [
        { label: 'Company Name', value: vendor?.company_name || 'N/A', key: 'company_name' },
        ...(distinctLegalName(vendor?.company_name, vendor?.gst_legal_name)
          ? [{ label: 'Legal Name (GST)', value: vendor.gst_legal_name, key: 'gst_legal_name' }]
          : []),
        {
          label: 'Business Vertical',
          value: formatBusinessVertical(vendor?.business_vertical),
          key: 'business_vertical',
        },
        // Chosen on the Invite Vendor form (approval routing); absent for vendors invited before it existed.
        { label: 'Nature of Business', value: vendor?.nature_of_business ? natureLabel(vendor.nature_of_business) : 'Not specified', key: 'nature_of_business' },
        { label: 'Supplier Type (OEM / ODM)', value: vendor?.oem_odm ? oemOdmLabel(vendor.oem_odm) : 'Not specified', key: 'oem_odm' },
        { label: 'Supplier Sub-category', value: vendor?.supplier_subcategory ? subcategoryLabel(vendor.supplier_subcategory) : 'Not specified', key: 'supplier_subcategory' },
        { label: 'Year Established', value: vendor?.year_established || 'N/A', key: 'year_established' },
        {
          label: 'Business Description',
          value: vendor?.business_description || 'N/A',
          key: 'business_description',
        },
      ],
    },
    registrationInfo: {
      title: 'Registration Information',
      icon: 'FileText',
      fields: [
        {
          label: 'Country of Origin',
          value: getCountryName(vendor?.country_origin) || 'N/A',
          key: 'country_origin',
        },
        ...(isIndia(vendor?.country_origin)
          ? [
              { label: 'PAN Number', value: vendor?.pan_number || 'N/A', key: 'pan_number' },
              { label: 'GST Number', value: vendor?.gst_number || 'N/A', key: 'gst_number' },
            ]
          : [
              {
                label: 'Tax Registration Number',
                value: vendor?.tax_registration_number || 'N/A',
                key: 'tax_registration_number',
              },
              { label: 'VAT/Sales Tax Number', value: vendor?.vat_number || 'N/A', key: 'vat_number' },
              {
                label: 'Business License Number',
                value: vendor?.business_license || 'N/A',
                key: 'business_license',
              },
            ]),
        { label: 'MSME Status', value: formatMsmeStatus(vendor?.msme_status), key: 'msme_status' },
        { label: 'MSME Number', value: vendor?.msme_number || 'N/A', key: 'msme_number' },
        {
          label: 'Classification Date',
          value: vendor?.classification_date
            ? new Date(vendor.classification_date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })
            : 'N/A',
          key: 'classification_date',
        },
        { label: 'Classification Year', value: vendor?.classification_year || 'N/A', key: 'classification_year' },
      ],
    },
    addressInfo: {
      title: 'Address Information',
      icon: 'MapPin',
      fields: [
        {
          label: 'Registered Address',
          value: vendor?.registered_address || 'N/A',
          key: 'registered_address',
          multiline: true,
        },
        { label: 'Registered City', value: vendor?.registered_city || 'N/A', key: 'registered_city' },
        { label: 'Registered State', value: vendor?.registered_state || 'N/A', key: 'registered_state' },
        { label: 'Registered Country', value: vendor?.registered_country || 'N/A', key: 'registered_country' },
        { label: 'Registered Pincode', value: vendor?.registered_pincode || 'N/A', key: 'registered_pincode' },
        {
          label: 'Supply Address',
          value: vendor?.supply_address || 'N/A',
          key: 'supply_address',
          multiline: true,
        },
        { label: 'Supply City', value: vendor?.supply_city || 'N/A', key: 'supply_city' },
        { label: 'Supply State', value: vendor?.supply_state || 'N/A', key: 'supply_state' },
        { label: 'Supply Country', value: vendor?.supply_country || 'N/A', key: 'supply_country' },
        { label: 'Supply Pincode', value: vendor?.supply_pincode || 'N/A', key: 'supply_pincode' },
      ],
    },
    businessInfo: {
      title: 'Business Information',
      icon: 'Briefcase',
      fields: [
        { label: 'Employee Count', value: formatEmployeeCount(vendor?.employee_count), key: 'employee_count' },
        { label: 'Annual Turnover', value: formatAnnualTurnover(vendor?.annual_turnover), key: 'annual_turnover' },
        {
          label: 'Supplier Group',
          value: formatSupplierGroup(vendor?.supplier_group),
          key: 'supplier_group_vendor',
        },
        {
          label: 'Supplier Category',
          value: formatSupplierCategory(vendor?.supplier_category),
          key: 'supplier_category',
        },
      ],
    },
    paymentInfo: {
      title: 'Payment Information',
      icon: 'CreditCard',
      fields: [
        {
          label: 'Terms of Payment',
          value: formatPaymentTerms(questionnaireData.supplierTermOfPayment),
          key: 'supplier_term_of_payment',
        },
        {
          label: 'Payment Method',
          value: formatPaymentMethod(questionnaireData.supplierPaymentMethod),
          key: 'supplier_payment_method',
        },
      ],
    },
    deliveryInfo: {
      title: 'Delivery Information',
      icon: 'Truck',
      fields: [
        {
          label: 'Delivery Terms',
          value: formatDeliveryTerms(questionnaireData.supplierDeliveryTerms),
          key: 'supplier_delivery_terms',
        },
        {
          label: 'Mode of Delivery',
          value: formatDeliveryMode(questionnaireData.supplierModeOfDelivery),
          key: 'supplier_mode_of_delivery',
        },
      ],
    },
    supplierClassification: {
      title: 'Supplier Classification',
      icon: 'Tag',
      fields: [
        {
          label: 'Supplier Group',
          value: formatSupplierGroupLocal(questionnaireData.supplierGroup),
          key: 'supplier_group_questionnaire',
        },
        {
          label: 'Commodity Code',
          value: formatCommodityCode(questionnaireData.commodityCode),
          key: 'commodity_code',
        },
      ],
    },
  };

  const renderField = (field) => {
    if (field.multiline) {
      return (
        <div key={field.key} className="space-y-2">
          <label className="text-sm font-medium text-slate-600">{field.label}:</label>
          <div className="bg-slate-50 p-3 rounded-md border border-slate-200">
            <p className="text-sm text-slate-900 whitespace-pre-wrap">{field.value}</p>
          </div>
        </div>
      );
    }

    return (
      <div key={field.key} className="flex justify-between items-center py-2 gap-4">
        <span className="text-slate-600 shrink-0">{field.label}:</span>
        <span className="font-medium text-slate-900 text-right">{field.value}</span>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {Object.entries(companyDetails).map(([sectionKey, section]) => (
          <Card key={sectionKey} className="border border-slate-200 shadow-sm bg-white">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Icon name={section.icon} size={20} className="text-slate-600" />
                {section.title}
              </h3>
              <div className="space-y-3">{section.fields.map((field) => renderField(field))}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border border-slate-200 shadow-sm bg-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Icon name="Info" size={20} className="text-slate-600" />
            Additional Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between gap-4">
                <span className="text-slate-600">Website:</span>
                {vendor?.website ? (
                  <a
                    href={getWebsiteUrl(vendor.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 hover:underline break-all text-right"
                  >
                    {vendor.website}
                  </a>
                ) : (
                  <span className="font-medium text-slate-900">N/A</span>
                )}
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-600">Email:</span>
                <span className="font-medium text-slate-900 text-right break-all">{vendor?.email || 'N/A'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-600">Phone:</span>
                <span className="font-medium text-slate-900">{vendor?.phone_number || 'N/A'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-600">Contact Person:</span>
                <span className="font-medium text-slate-900 text-right">{vendor?.contact_person_name || 'N/A'}</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between gap-4">
                <span className="text-slate-600">Designation:</span>
                <span className="font-medium text-slate-900 text-right">{vendor?.designation || 'N/A'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-600">Credit Rating:</span>
                <span className="font-medium text-slate-900 text-right">{vendor?.credit_rating || 'N/A'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-600">Insurance Coverage:</span>
                <span className="font-medium text-slate-900 text-right">{vendor?.insurance_coverage || 'N/A'}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-slate-600">GTA Registration:</span>
                <span className="font-medium text-slate-900 text-right">{vendor?.gta_registration || 'N/A'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanyDetailsTab;
